import useProvider from "../../hooks/useProvider";
import { ethers } from "ethers";
import CONTRACT_INFO from "../../ContractInfo";
import React, { useContext, useEffect, useState } from "react";
import { ReactComponent as AddIcon } from "../../assets/icons/add.svg";
import { ReactComponent as SubtractIcon } from "../../assets/icons/subtract.svg";
import { AppContext } from "../../context";
import Navbar from "../Navbar";
import styles from "./Marketplace.module.scss";
import Modal from "../Modal/Modal";
import Footer from "../Footer";

export const gmPointsMap = {
  1: 5,
  2: 2,
  3: 1,
  4: 2,
  5: 5,
  6: 20,
};

type GMTokenId = keyof typeof gmPointsMap;

type Balances = { [K in GMTokenId]: number };

const getImageUrl = (gmIdString: string) =>
  `https://gms-metadata.s3.eu-central-1.amazonaws.com/polygon/${gmIdString}.png`;

const Marketplace = () => {
  const initialBalances: Balances = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
    6: 0,
  };
  const [balances, setBalances] = useState<Balances>(initialBalances);
  const [cart, setCart] = useState<Balances>(initialBalances);
  const cartPoints = Object.entries(cart).reduce(
    (sum, [gmIdString, balance]) => {
      const gmId = Number(gmIdString) as GMTokenId;
      return cart[gmId] * gmPointsMap[gmId] + sum;
    },
    0
  );
  const { wallet, connect } = useContext(AppContext);
  const provider = useProvider();
  const gmsContract = new ethers.Contract(
    CONTRACT_INFO[window.chain].contract,
    CONTRACT_INFO[window.chain].abi,
    provider?.getSigner()
  );

  // modal
  const [modalMessage, setModalMessage] = useState("");

  const getTotalPolygonPoints = async (walletAddress: string) => {
    const gmIds = Object.keys(gmPointsMap);
    const balances = await gmsContract.balanceOfBatch(
      gmIds.map(() => walletAddress),
      gmIds
    );
    const balancesInNumbers = balances.map((bigInt: BigInteger) =>
      Number(bigInt.toString())
    );
    const newBalances: Balances = { ...initialBalances };

    balancesInNumbers.forEach((balance: number, index: number) => {
      const gmId = Number(gmIds[index]) as GMTokenId;
      newBalances[gmId] = balance;
    });
    setBalances(newBalances);
  };

  const redeemFreeMint = async () => {
    const numOfFreeMints = Math.floor(cartPoints / 20);
    const tx = await gmsContract.burnTokens(
      Object.keys(cart).map((gmIdString) => Number(gmIdString)),
      Object.values(cart)
    );
    setModalMessage(`Waiting for transaction to complete...`);
    await tx.wait();
    setModalMessage(
      `Transaction complete. ${numOfFreeMints} free mints have been added for your wallet!`
    );
  };

  useEffect(() => {
    connect();
    if (wallet) {
      getTotalPolygonPoints(wallet);
    }
  }, [wallet]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAddButtonDisabled = (gmId: GMTokenId): boolean => {
    const userHasBalance = balances[gmId] - cart[gmId] > 0;
    return !userHasBalance;
  };

  const isSubtractButtonDisabled = (gmId: GMTokenId): boolean =>
    cart[gmId] <= 0;

  const addToCart = (gmId: GMTokenId) => {
    if (!isAddButtonDisabled(gmId)) {
      setCart({
        ...cart,
        [gmId]: cart[gmId] + 1,
      });
    }
  };

  const removeFromCart = (gmId: GMTokenId) => {
    if (!isSubtractButtonDisabled(gmId)) {
      setCart({
        ...cart,
        [gmId]: cart[gmId] - 1,
      });
    }
  };

  return (
    <div className={styles.marketplace}>
      <Navbar />
      <div className={styles.page}>
        <div className={styles.title}>
          Redeem <span className="yellow">Free</span> Mint
        </div>
        <div className={styles.subtitle}>Every 20 points = 1 free mint</div>
        <div className={styles.bodyContainer}>
          <div className={styles.main}>
            {Number(window.ethereum.networkVersion) ===
            CONTRACT_INFO[window.chain].chainId ? (
              <div className={styles.gmsList}>
                {balances &&
                  Object.entries(balances).map(([gmIdString, balance]) => {
                    const gmId = Number(gmIdString) as GMTokenId;
                    return (
                      <div className={styles.gm} key={gmIdString}>
                        <img
                          src={getImageUrl(gmIdString)}
                          alt={`GM #${gmIdString}`}
                          // width={250}
                        />
                        <div className={styles.actionButtons}>
                          <SubtractIcon
                            className={`${styles.actionButton} ${
                              isSubtractButtonDisabled(gmId)
                                ? styles.disabledButton
                                : ""
                            }`}
                            onClick={() => removeFromCart(gmId)}
                          />
                          <AddIcon
                            className={`${styles.actionButton} ${
                              isAddButtonDisabled(gmId)
                                ? styles.disabledButton
                                : ""
                            }`}
                            onClick={() => addToCart(gmId)}
                          />
                        </div>
                        <div className={styles.gmInfo}>
                          <h1>GM #{gmIdString}</h1>
                          <div>Points: {gmPointsMap[gmId]}</div>
                          <div>You have: {balances[gmId]}</div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div>Please change network to Polygon to claim your GMs!</div>
            )}
          </div>
          <div className={styles.cart}>
            <div className={styles.cartTitle}>Your Cart</div>
            <div className={styles.cartItems}>
              {Object.entries(cart)
                .filter(([gmId, amount]) => amount > 0)
                .map(([gmIdString, amount]) => {
                  const gmId = Number(gmIdString) as GMTokenId;
                  return (
                    <div key={gmIdString} className={styles.cartItem}>
                      <img
                        src={getImageUrl(gmIdString)}
                        width={100}
                        alt={`GM #${gmIdString}`}
                      />
                      <div>
                        <div>Amount: {cart[gmId]}</div>
                        <div>Points: {cart[gmId] * gmPointsMap[gmId]}</div>
                      </div>
                    </div>
                  );
                })}
            </div>
            <button
              className={styles.burnButton}
              disabled={cartPoints < 20}
              onClick={redeemFreeMint}
            >
              Burn {cartPoints} Points
            </button>
            <div>Excess points burnt will be wasted</div>
          </div>
        </div>
      </div>
      <Modal
        isActive={Boolean(modalMessage)}
        onClose={() => setModalMessage("")}
        title={<div>collect gm points</div>}
        content={<div>{modalMessage}</div>}
      />
      <Footer />
    </div>
  );
};

export default Marketplace;
